import { IAvailablePlanNames } from 'client/components/Subscription/PremiumPlans/IPremiumInterface'
import { getPlanQuota } from 'client/components/Subscription/PremiumPlans/plansSelector'
import { StateInterface } from 'client/Interfaces/StateInterface'
import { QuotaTypes, SubscriptionPlanInterface } from 'client/Interfaces/Subscription'
import { SubscriptionFeatureCheckboxType } from 'client/Interfaces/Subscription/ISubscriptionFeatureList'
// typescript
import {
  AvailableSubscriptionPlansStateType,
  SubscriptionManagementStateInterface,
} from 'client/Interfaces/Subscription/ReduxStateInterfaces'
import { createSelector } from 'reselect'

// selectors
import { subscriptionAvailablePlansSelector } from '.'

// extracts plan id name and version
// ex plan id: fi_starter = {name: fi_starter, version: 0}
//             fi_starter_2 = {name: fi_starter, version: 2}
export const getPlanIdNameAndVersionSuffix = ({ id }: SubscriptionPlanInterface) => {
  const regexMatch = /(.*?)(?=_\d|$)[_]?(\d+$)?/.exec(id)
  return {
    id: regexMatch[1] || false,
    version: Number(regexMatch[2]) || 0,
  }
}

export const sortPlansByPrice = (plans): SubscriptionPlanInterface[] => {
  return plans.sort((a, b) => a.price - b.price)
}

// when there is plan.id called fi_starter_2 and plans collection also has fi_starter_3, then only fi_starter_3 will
// be displayed, as _3 is higher than _2
export const replaceOldPlanWithNew = (plansCollection: SubscriptionPlanInterface[]) => {
  return plansCollection.reduce((plansCollection, plan, planIndex) => {
    const { id: currId, version: currVersion } = getPlanIdNameAndVersionSuffix(plan)

    // find a plan with the same id name (removing any version suffix)
    const matchedCurrentPlanIndex = plansCollection.findIndex(({ id, billingCycle: matchingBillingCycle }) => {
      const matchingId = id.replace(/(_\d+$)/, '')
      return plan.billingCycle === matchingBillingCycle && matchingId === currId
    })

    if (matchedCurrentPlanIndex === -1) {
      return plansCollection
    }

    const matchedCurrentPlan = plansCollection[matchedCurrentPlanIndex]

    // skip if found self from plansCollection
    if (matchedCurrentPlan.id === plan.id) {
      return plansCollection
    }

    const { version: matchedIdVersion } = getPlanIdNameAndVersionSuffix(matchedCurrentPlan)

    const planIndexToRemove = matchedIdVersion > currVersion ? planIndex : matchedCurrentPlanIndex

    return plansCollection.splice(planIndexToRemove, 1) && plansCollection
  }, plansCollection)
}

// create custom plan called "contact us", used to display "contact us" plan to receive custom quota.
export const getPlansContactUs = (plans: SubscriptionPlanInterface[]) => {
  // plans are sorted by highest price, last plan means highest invoice count in this case.. so we use that
  return ['month', 'year'].map((billingCycle) => {
    const plansByBillingCycle = plans?.filter((plan) => plan.billingCycle === billingCycle)
    const highestInvoice = getPlanQuota(plansByBillingCycle[plansByBillingCycle.length - 1], QuotaTypes.INVOICE)

    return {
      billingCycle,
      id: 'plan.contact_us',
      name: 'plan.contact_us',
      price: 0,
      currency: '€',
      quotas: [
        { name: QuotaTypes.INVOICE, quota: highestInvoice },
        { name: QuotaTypes.CUSTOMERS, quota: 'Unlimited' },
      ],
    } as SubscriptionPlanInterface
  })
}

// returns availablePlans, also sorts and removes any duplicates if there are multiple versions of the same plan id
export const subscriptionAvailablePlansCollectionSelector = createSelector<
  StateInterface,
  SubscriptionManagementStateInterface['availablePlans'],
  SubscriptionPlanInterface[]
>(subscriptionAvailablePlansSelector, (availablePlans) => {
  let plansCollection = availablePlans && availablePlans.collection ? [...availablePlans.collection] : []

  plansCollection = sortPlansByPrice(replaceOldPlanWithNew(plansCollection))

  const isAgerasPlanCollection = plansCollection.some((plan) => plan.id.includes('ageras'))

  const freePlanName = `plan.${isAgerasPlanCollection ? 'ageras_' : ''}free`

  if (isAgerasPlanCollection) {
    plansCollection.unshift({
      id: freePlanName,
      name: IAvailablePlanNames[freePlanName],
      price: 0,
      currency: '€',
      quotas: [
        { name: QuotaTypes.CUSTOMERS, quota: 5 },
        { name: QuotaTypes.LIMITED_INVOICES_ESTIMATES, quota: 5 },
        { name: QuotaTypes.E_INVOICE, quota: null, checkboxStyle: SubscriptionFeatureCheckboxType.DASH },
        { name: QuotaTypes.INVOICE, quota: null, checkboxStyle: SubscriptionFeatureCheckboxType.DASH },
        { name: QuotaTypes.FREE_ESTIMATES },
        { name: QuotaTypes.TIMESHEET },
        { name: QuotaTypes.EXPENSES, quota: 10 },
        { name: QuotaTypes.BANK_RECONCILIATION },
        { name: QuotaTypes.AI_EXPENSES_CATEGORIZATION },
        { name: QuotaTypes.ONLINE_CARD_PAYMENTS },
        { name: QuotaTypes.ONLINE_BANK_PAYMENTS },
      ],
      billingCycle: 'month',
    })

    plansCollection.unshift({
      id: freePlanName,
      name: IAvailablePlanNames[freePlanName],
      price: 0,
      currency: '€',
      quotas: [
        { name: QuotaTypes.CUSTOMERS, quota: 5 },
        { name: QuotaTypes.LIMITED_INVOICES_ESTIMATES, quota: 5 },
        { name: QuotaTypes.E_INVOICE, quota: null, checkboxStyle: SubscriptionFeatureCheckboxType.DASH },
        { name: QuotaTypes.INVOICE, quota: null, checkboxStyle: SubscriptionFeatureCheckboxType.DASH },
        { name: QuotaTypes.FREE_ESTIMATES },
        { name: QuotaTypes.TIMESHEET },
        { name: QuotaTypes.EXPENSES, quota: 10 },
        { name: QuotaTypes.BANK_RECONCILIATION },
        { name: QuotaTypes.AI_EXPENSES_CATEGORIZATION },
        { name: QuotaTypes.ONLINE_CARD_PAYMENTS },
        { name: QuotaTypes.ONLINE_BANK_PAYMENTS },
      ],
      billingCycle: 'year',
    })
  } else {
    plansCollection.unshift({
      id: freePlanName,
      name: IAvailablePlanNames[freePlanName],
      price: 0,
      currency: '€',
      quotas: [
        { name: QuotaTypes.INVOICE, quota: null, checkboxStyle: SubscriptionFeatureCheckboxType.DISABLED },
        { name: QuotaTypes.CUSTOMERS, quota: 5 },
        { name: QuotaTypes.LIMITED_INVOICES_ESTIMATES, quota: 5 },
      ],
      billingCycle: 'month',
    })

    plansCollection.unshift({
      id: freePlanName,
      name: IAvailablePlanNames[freePlanName],
      price: 0,
      currency: '€',
      quotas: [
        { name: QuotaTypes.INVOICE, quota: null, checkboxStyle: SubscriptionFeatureCheckboxType.DISABLED },
        { name: QuotaTypes.CUSTOMERS, quota: 5 },
        { name: QuotaTypes.LIMITED_INVOICES_ESTIMATES, quota: 5 },
      ],
      billingCycle: 'year',
    })
  }

  plansCollection.push(...getPlansContactUs(plansCollection))

  return plansCollection
})
